import { useState, useMemo, useCallback } from 'react';
import { useForm, router } from '@inertiajs/react';
import { AnimatePresence, motion } from 'framer-motion';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { toast, Toaster } from 'sonner';
import { useHotkeys } from 'react-hotkeys-hook';
import { format, parseISO, isAfter, isBefore, addDays } from 'date-fns';
import 'react-datepicker/dist/react-datepicker.css';

const STATUSES = {
    pending: { label: 'Pending', color: 'warning', icon: 'bi-clock' },
    received: { label: 'Received', color: 'info', icon: 'bi-check-circle' },
    verified: { label: 'Verified', color: 'success', icon: 'bi-check2-all' },
    rejected: { label: 'Rejected', color: 'danger', icon: 'bi-x-circle' },
    cancelled: { label: 'Cancelled', color: 'secondary', icon: 'bi-slash-circle' },
    refunded: { label: 'Refunded', color: 'primary', icon: 'bi-arrow-counterclockwise' },
};

const TYPES = {
    scheduled: { label: 'Scheduled', color: 'primary', icon: 'bi-calendar-check' },
    received: { label: 'Received', color: 'success', icon: 'bi-cash-stack' },
};

const PAYMENT_METHODS = {
    cash: { label: 'Cash', icon: 'bi-cash' },
    bank_transfer: { label: 'Bank Transfer', icon: 'bi-bank' },
    upi: { label: 'UPI', icon: 'bi-phone' },
    card: { label: 'Card', icon: 'bi-credit-card' },
    cheque: { label: 'Cheque', icon: 'bi-file-earmark-text' },
    online: { label: 'Online', icon: 'bi-globe' },
    other: { label: 'Other', icon: 'bi-three-dots' },
};

const CHART_COLORS = ['#6366f1', '#22c55e', '#eab308', '#ef4444', '#64748b', '#3b82f6'];

export default function PaymentTab({ query, payments = [], quotations = [], currencies = [] }) {
    const [showForm, setShowForm] = useState(false);
    const [editingPayment, setEditingPayment] = useState(null);
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [selectedPayments, setSelectedPayments] = useState([]);
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterType, setFilterType] = useState('all');

    // Keyboard shortcuts
    useHotkeys('ctrl+n, cmd+n', (e) => {
        e.preventDefault();
        handleAddPayment();
    }, { enableOnFormTags: false });

    useHotkeys('escape', () => {
        setShowForm(false);
        setEditingPayment(null);
        setShowStatusModal(false);
        setShowScheduleModal(false);
    });

    // Form state
    const { data, setData, post, put, processing, errors, reset } = useForm({
        type: 'scheduled',
        amount: '',
        currency_id: currencies[0]?.id || '',
        exchange_rate: '1.0000',
        payment_method: '',
        payment_date: null,
        due_date: null,
        reference_number: '',
        bank_name: '',
        cheque_number: '',
        cheque_date: null,
        transaction_id: '',
        invoice_number: '',
        notes: '',
    });

    // Schedule form state
    const scheduleForm = useForm({
        quotation_id: '',
        installments: 1,
        first_due_date: new Date(),
        interval_days: 30,
    });

    // Calculate analytics
    const analytics = useMemo(() => {
        const totalScheduled = payments
            .filter(p => p.type === 'scheduled')
            .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);

        const totalReceived = payments
            .filter(p => ['received', 'verified'].includes(p.status))
            .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);

        const totalPending = payments
            .filter(p => p.status === 'pending')
            .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);

        const overduePayments = payments.filter(p => {
            if (p.status !== 'pending' || !p.due_date) return false;
            return isBefore(parseISO(p.due_date), new Date());
        });

        const totalOverdue = overduePayments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);

        const byStatus = Object.keys(STATUSES).map(status => ({
            name: STATUSES[status].label,
            value: payments.filter(p => p.status === status).length,
            amount: payments.filter(p => p.status === status).reduce((sum, p) => sum + parseFloat(p.amount || 0), 0),
        })).filter(s => s.value > 0);

        const byMethod = Object.keys(PAYMENT_METHODS).map(method => ({
            name: PAYMENT_METHODS[method].label,
            value: payments.filter(p => p.payment_method === method).length,
            amount: payments.filter(p => p.payment_method === method).reduce((sum, p) => sum + parseFloat(p.amount || 0), 0),
        })).filter(m => m.value > 0);

        return {
            totalScheduled,
            totalReceived,
            totalPending,
            totalOverdue,
            overdueCount: overduePayments.length,
            byStatus,
            byMethod,
            percentReceived: totalScheduled > 0 ? ((totalReceived / totalScheduled) * 100).toFixed(1) : 0,
        };
    }, [payments]);

    // Filter payments
    const filteredPayments = useMemo(() => {
        return payments.filter(payment => {
            if (filterStatus !== 'all' && payment.status !== filterStatus) return false;
            if (filterType !== 'all' && payment.type !== filterType) return false;
            return true;
        });
    }, [payments, filterStatus, filterType]);

    // Currency options for react-select
    const currencyOptions = currencies.map(c => ({
        value: c.id,
        label: `${c.code} - ${c.name}`,
        symbol: c.symbol,
    }));

    // Quotation options for scheduling
    const quotationOptions = quotations
        .filter(q => parseFloat(q.total_selling || q.total_cost || 0) > 0)
        .map(q => ({
            value: q.id,
            label: `${q.quotation_number} - ${q.title || 'Quotation'} (${formatCurrency(q.total_selling || q.total_cost)})`,
        }));

    const handleAddPayment = useCallback(() => {
        reset();
        setEditingPayment(null);
        setShowForm(true);
    }, [reset]);

    const handleEditPayment = useCallback((payment) => {
        setData({
            type: payment.type,
            amount: payment.amount,
            currency_id: payment.currency_id || '',
            exchange_rate: payment.exchange_rate || '1.0000',
            payment_method: payment.payment_method || '',
            payment_date: payment.payment_date ? parseISO(payment.payment_date) : null,
            due_date: payment.due_date ? parseISO(payment.due_date) : null,
            reference_number: payment.reference_number || '',
            bank_name: payment.bank_name || '',
            cheque_number: payment.cheque_number || '',
            cheque_date: payment.cheque_date ? parseISO(payment.cheque_date) : null,
            transaction_id: payment.transaction_id || '',
            invoice_number: payment.invoice_number || '',
            notes: payment.notes || '',
        });
        setEditingPayment(payment);
        setShowForm(true);
    }, [setData]);

    const handleSubmit = (e) => {
        e.preventDefault();

        const formData = {
            ...data,
            payment_date: data.payment_date ? format(data.payment_date, 'yyyy-MM-dd') : null,
            due_date: data.due_date ? format(data.due_date, 'yyyy-MM-dd') : null,
            cheque_date: data.cheque_date ? format(data.cheque_date, 'yyyy-MM-dd') : null,
        };

        if (editingPayment) {
            router.put(`/queries/${query.id}/payments/${editingPayment.id}`, formData, {
                onSuccess: () => {
                    toast.success('Payment updated successfully');
                    setShowForm(false);
                    setEditingPayment(null);
                    reset();
                },
                onError: () => toast.error('Failed to update payment'),
            });
        } else {
            router.post(`/queries/${query.id}/payments`, formData, {
                onSuccess: () => {
                    toast.success('Payment recorded successfully');
                    setShowForm(false);
                    reset();
                },
                onError: () => toast.error('Failed to record payment'),
            });
        }
    };

    const handleDelete = useCallback((payment) => {
        if (confirm('Are you sure you want to delete this payment?')) {
            router.delete(`/queries/${query.id}/payments/${payment.id}`, {
                onSuccess: () => toast.success('Payment deleted'),
                onError: () => toast.error('Failed to delete payment'),
            });
        }
    }, [query.id]);

    const handleStatusChange = useCallback((payment) => {
        setSelectedPayment(payment);
        setShowStatusModal(true);
    }, []);

    const submitStatusChange = (status, rejectionReason = '') => {
        router.patch(`/queries/${query.id}/payments/${selectedPayment.id}/status`, {
            status,
            rejection_reason: rejectionReason,
        }, {
            onSuccess: () => {
                toast.success('Status updated');
                setShowStatusModal(false);
                setSelectedPayment(null);
            },
            onError: () => toast.error('Failed to update status'),
        });
    };

    const handleSchedulePayments = () => {
        const formData = {
            ...scheduleForm.data,
            first_due_date: format(scheduleForm.data.first_due_date, 'yyyy-MM-dd'),
        };

        router.post(`/queries/${query.id}/payments/from-quotation`, formData, {
            onSuccess: () => {
                toast.success('Payment schedule created');
                setShowScheduleModal(false);
                scheduleForm.reset();
            },
            onError: () => toast.error('Failed to create payment schedule'),
        });
    };

    const handleBulkStatusChange = (status) => {
        if (selectedPayments.length === 0) {
            toast.error('Select payments first');
            return;
        }

        router.post(`/queries/${query.id}/payments/bulk-status`, {
            ids: selectedPayments,
            status,
        }, {
            onSuccess: () => {
                toast.success(`${selectedPayments.length} payments updated`);
                setSelectedPayments([]);
            },
            onError: () => toast.error('Failed to update payments'),
        });
    };

    const handleBulkDelete = () => {
        if (selectedPayments.length === 0) {
            toast.error('Select payments first');
            return;
        }

        if (confirm(`Delete ${selectedPayments.length} payments?`)) {
            router.post(`/queries/${query.id}/payments/bulk-delete`, {
                ids: selectedPayments,
            }, {
                onSuccess: () => {
                    toast.success(`${selectedPayments.length} payments deleted`);
                    setSelectedPayments([]);
                },
                onError: () => toast.error('Failed to delete payments'),
            });
        }
    };

    const togglePaymentSelection = (id) => {
        setSelectedPayments(prev =>
            prev.includes(id)
                ? prev.filter(p => p !== id)
                : [...prev, id]
        );
    };

    const toggleAllPayments = () => {
        if (selectedPayments.length === filteredPayments.length) {
            setSelectedPayments([]);
        } else {
            setSelectedPayments(filteredPayments.map(p => p.id));
        }
    };

    const formatCurrency = (amount, symbol = '₹') => {
        return `${symbol}${parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
    };

    const isOverdue = (payment) => {
        if (payment.status !== 'pending' || !payment.due_date) return false;
        return isBefore(parseISO(payment.due_date), new Date());
    };

    return (
        <div className="payment-tab">
            <Toaster position="top-right" richColors />

            {/* Header with Actions */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h5 className="mb-1">Payment Tracking</h5>
                    <small className="text-muted">
                        <kbd>Ctrl+N</kbd> to add payment, <kbd>Esc</kbd> to close forms
                    </small>
                </div>
                <div className="d-flex gap-2">
                    {quotationOptions.length > 0 && (
                        <button
                            className="btn btn-outline-primary"
                            onClick={() => setShowScheduleModal(true)}
                        >
                            <i className="bi bi-calendar-plus me-1"></i>
                            Schedule from Quotation
                        </button>
                    )}
                    <button
                        className="btn btn-primary"
                        onClick={handleAddPayment}
                    >
                        <i className="bi bi-plus-lg me-1"></i>
                        Add Payment
                    </button>
                </div>
            </div>

            {/* Analytics Cards */}
            <div className="row g-3 mb-4">
                <div className="col-md-3">
                    <div className="card border-0 bg-primary-subtle h-100">
                        <div className="card-body">
                            <div className="d-flex align-items-center">
                                <div className="flex-shrink-0">
                                    <div className="rounded-circle bg-primary d-flex align-items-center justify-content-center" style={{ width: 48, height: 48 }}>
                                        <i className="bi bi-wallet2 text-white fs-5"></i>
                                    </div>
                                </div>
                                <div className="flex-grow-1 ms-3">
                                    <h6 className="mb-0 text-muted">Total Scheduled</h6>
                                    <h4 className="mb-0">{formatCurrency(analytics.totalScheduled)}</h4>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card border-0 bg-success-subtle h-100">
                        <div className="card-body">
                            <div className="d-flex align-items-center">
                                <div className="flex-shrink-0">
                                    <div className="rounded-circle bg-success d-flex align-items-center justify-content-center" style={{ width: 48, height: 48 }}>
                                        <i className="bi bi-check2-all text-white fs-5"></i>
                                    </div>
                                </div>
                                <div className="flex-grow-1 ms-3">
                                    <h6 className="mb-0 text-muted">Total Received</h6>
                                    <h4 className="mb-0">{formatCurrency(analytics.totalReceived)}</h4>
                                    <small className="text-success">{analytics.percentReceived}% collected</small>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card border-0 bg-warning-subtle h-100">
                        <div className="card-body">
                            <div className="d-flex align-items-center">
                                <div className="flex-shrink-0">
                                    <div className="rounded-circle bg-warning d-flex align-items-center justify-content-center" style={{ width: 48, height: 48 }}>
                                        <i className="bi bi-clock text-white fs-5"></i>
                                    </div>
                                </div>
                                <div className="flex-grow-1 ms-3">
                                    <h6 className="mb-0 text-muted">Pending</h6>
                                    <h4 className="mb-0">{formatCurrency(analytics.totalPending)}</h4>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card border-0 bg-danger-subtle h-100">
                        <div className="card-body">
                            <div className="d-flex align-items-center">
                                <div className="flex-shrink-0">
                                    <div className="rounded-circle bg-danger d-flex align-items-center justify-content-center" style={{ width: 48, height: 48 }}>
                                        <i className="bi bi-exclamation-triangle text-white fs-5"></i>
                                    </div>
                                </div>
                                <div className="flex-grow-1 ms-3">
                                    <h6 className="mb-0 text-muted">Overdue</h6>
                                    <h4 className="mb-0">{formatCurrency(analytics.totalOverdue)}</h4>
                                    <small className="text-danger">{analytics.overdueCount} payments</small>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Charts Row */}
            {payments.length > 0 && (
                <div className="row g-3 mb-4">
                    <div className="col-md-6">
                        <div className="card border-0 shadow-sm h-100">
                            <div className="card-header bg-white border-0">
                                <h6 className="mb-0">Payments by Status</h6>
                            </div>
                            <div className="card-body">
                                <ResponsiveContainer width="100%" height={200}>
                                    <PieChart>
                                        <Pie
                                            data={analytics.byStatus}
                                            dataKey="value"
                                            nameKey="name"
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={40}
                                            outerRadius={80}
                                            paddingAngle={2}
                                            label={({ name, value }) => `${name}: ${value}`}
                                        >
                                            {analytics.byStatus.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip formatter={(value, name, props) => [formatCurrency(props.payload.amount), name]} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="card border-0 shadow-sm h-100">
                            <div className="card-header bg-white border-0">
                                <h6 className="mb-0">Payments by Method</h6>
                            </div>
                            <div className="card-body">
                                <ResponsiveContainer width="100%" height={200}>
                                    <BarChart data={analytics.byMethod} layout="vertical">
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis type="number" />
                                        <YAxis type="category" dataKey="name" width={100} />
                                        <Tooltip formatter={(value, name, props) => [formatCurrency(props.payload.amount), 'Amount']} />
                                        <Bar dataKey="value" fill="#6366f1" radius={[0, 4, 4, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Filters and Bulk Actions */}
            <div className="card border-0 shadow-sm mb-4">
                <div className="card-body py-2">
                    <div className="d-flex flex-wrap align-items-center gap-3">
                        <div className="d-flex align-items-center gap-2">
                            <label className="form-label mb-0 small">Status:</label>
                            <select
                                className="form-select form-select-sm"
                                style={{ width: 130 }}
                                value={filterStatus}
                                onChange={(e) => setFilterStatus(e.target.value)}
                            >
                                <option value="all">All Status</option>
                                {Object.entries(STATUSES).map(([key, { label }]) => (
                                    <option key={key} value={key}>{label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <label className="form-label mb-0 small">Type:</label>
                            <select
                                className="form-select form-select-sm"
                                style={{ width: 130 }}
                                value={filterType}
                                onChange={(e) => setFilterType(e.target.value)}
                            >
                                <option value="all">All Types</option>
                                {Object.entries(TYPES).map(([key, { label }]) => (
                                    <option key={key} value={key}>{label}</option>
                                ))}
                            </select>
                        </div>

                        {selectedPayments.length > 0 && (
                            <div className="d-flex align-items-center gap-2 ms-auto">
                                <span className="badge bg-primary">{selectedPayments.length} selected</span>
                                <div className="btn-group">
                                    <button
                                        className="btn btn-sm btn-outline-success"
                                        onClick={() => handleBulkStatusChange('verified')}
                                    >
                                        <i className="bi bi-check2-all"></i> Verify
                                    </button>
                                    <button
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={handleBulkDelete}
                                    >
                                        <i className="bi bi-trash"></i> Delete
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Payments Table */}
            <div className="card border-0 shadow-sm">
                <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                        <thead className="bg-light">
                            <tr>
                                <th style={{ width: 40 }}>
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={selectedPayments.length === filteredPayments.length && filteredPayments.length > 0}
                                        onChange={toggleAllPayments}
                                    />
                                </th>
                                <th>Payment #</th>
                                <th>Type</th>
                                <th>Amount</th>
                                <th>Method</th>
                                <th>Due/Paid Date</th>
                                <th>Status</th>
                                <th style={{ width: 120 }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            <AnimatePresence mode="popLayout">
                                {filteredPayments.length === 0 ? (
                                    <tr>
                                        <td colSpan="8" className="text-center py-5">
                                            <i className="bi bi-wallet2 fs-1 text-muted d-block mb-3"></i>
                                            <h6>No payments recorded</h6>
                                            <p className="text-muted mb-3">
                                                {payments.length === 0
                                                    ? 'Start by adding a scheduled or received payment'
                                                    : 'No payments match the current filters'}
                                            </p>
                                            {payments.length === 0 && (
                                                <button
                                                    className="btn btn-primary"
                                                    onClick={handleAddPayment}
                                                >
                                                    <i className="bi bi-plus-lg me-1"></i>
                                                    Add Payment
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ) : (
                                    filteredPayments.map((payment) => (
                                        <motion.tr
                                            key={payment.id}
                                            initial={{ opacity: 0, y: -10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, x: -100 }}
                                            className={isOverdue(payment) ? 'table-danger' : ''}
                                        >
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={selectedPayments.includes(payment.id)}
                                                    onChange={() => togglePaymentSelection(payment.id)}
                                                />
                                            </td>
                                            <td>
                                                <div className="fw-medium">{payment.payment_number}</div>
                                                {payment.invoice_number && (
                                                    <small className="text-muted">Inv: {payment.invoice_number}</small>
                                                )}
                                            </td>
                                            <td>
                                                <span className={`badge bg-${TYPES[payment.type]?.color}-subtle text-${TYPES[payment.type]?.color}`}>
                                                    <i className={`bi ${TYPES[payment.type]?.icon} me-1`}></i>
                                                    {TYPES[payment.type]?.label}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="fw-semibold">
                                                    {formatCurrency(payment.amount, payment.currency?.symbol)}
                                                </div>
                                                {payment.currency && payment.currency.code !== 'INR' && (
                                                    <small className="text-muted">
                                                        @ {payment.exchange_rate}
                                                    </small>
                                                )}
                                            </td>
                                            <td>
                                                {payment.payment_method ? (
                                                    <span>
                                                        <i className={`bi ${PAYMENT_METHODS[payment.payment_method]?.icon} me-1`}></i>
                                                        {PAYMENT_METHODS[payment.payment_method]?.label}
                                                    </span>
                                                ) : (
                                                    <span className="text-muted">-</span>
                                                )}
                                            </td>
                                            <td>
                                                {payment.type === 'scheduled' && payment.due_date ? (
                                                    <div>
                                                        <div className={isOverdue(payment) ? 'text-danger fw-medium' : ''}>
                                                            {format(parseISO(payment.due_date), 'dd MMM yyyy')}
                                                        </div>
                                                        {isOverdue(payment) && (
                                                            <small className="text-danger">
                                                                <i className="bi bi-exclamation-circle me-1"></i>
                                                                Overdue
                                                            </small>
                                                        )}
                                                    </div>
                                                ) : payment.payment_date ? (
                                                    <div>{format(parseISO(payment.payment_date), 'dd MMM yyyy')}</div>
                                                ) : (
                                                    <span className="text-muted">-</span>
                                                )}
                                            </td>
                                            <td>
                                                <span
                                                    className={`badge bg-${STATUSES[payment.status]?.color}-subtle text-${STATUSES[payment.status]?.color} cursor-pointer`}
                                                    onClick={() => handleStatusChange(payment)}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <i className={`bi ${STATUSES[payment.status]?.icon} me-1`}></i>
                                                    {STATUSES[payment.status]?.label}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="btn-group">
                                                    <button
                                                        className="btn btn-sm btn-outline-primary"
                                                        onClick={() => handleEditPayment(payment)}
                                                        title="Edit"
                                                    >
                                                        <i className="bi bi-pencil"></i>
                                                    </button>
                                                    {payment.status === 'received' && (
                                                        <button
                                                            className="btn btn-sm btn-outline-success"
                                                            onClick={() => {
                                                                router.post(`/queries/${query.id}/payments/${payment.id}/verify`, {}, {
                                                                    onSuccess: () => toast.success('Payment verified'),
                                                                });
                                                            }}
                                                            title="Verify"
                                                        >
                                                            <i className="bi bi-check2-all"></i>
                                                        </button>
                                                    )}
                                                    <button
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() => handleDelete(payment)}
                                                        title="Delete"
                                                    >
                                                        <i className="bi bi-trash"></i>
                                                    </button>
                                                </div>
                                            </td>
                                        </motion.tr>
                                    ))
                                )}
                            </AnimatePresence>
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Payment Form Modal */}
            <AnimatePresence>
                {showForm && (
                    <motion.div
                        className="modal show d-block"
                        style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <div className="modal-dialog modal-lg modal-dialog-centered">
                            <motion.div
                                className="modal-content"
                                initial={{ scale: 0.9, y: 20 }}
                                animate={{ scale: 1, y: 0 }}
                                exit={{ scale: 0.9, y: 20 }}
                            >
                                <div className="modal-header border-0">
                                    <h5 className="modal-title">
                                        <i className="bi bi-wallet2 me-2"></i>
                                        {editingPayment ? 'Edit Payment' : 'Add Payment'}
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => {
                                            setShowForm(false);
                                            setEditingPayment(null);
                                            reset();
                                        }}
                                    ></button>
                                </div>
                                <form onSubmit={handleSubmit}>
                                    <div className="modal-body">
                                        <div className="row g-3">
                                            {/* Type */}
                                            <div className="col-md-6">
                                                <label className="form-label">Type *</label>
                                                <div className="btn-group w-100">
                                                    {Object.entries(TYPES).map(([key, { label, icon, color }]) => (
                                                        <button
                                                            key={key}
                                                            type="button"
                                                            className={`btn ${data.type === key ? `btn-${color}` : `btn-outline-${color}`}`}
                                                            onClick={() => setData('type', key)}
                                                        >
                                                            <i className={`bi ${icon} me-1`}></i>
                                                            {label}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Amount */}
                                            <div className="col-md-6">
                                                <label className="form-label">Amount *</label>
                                                <div className="input-group">
                                                    <span className="input-group-text">
                                                        {currencies.find(c => c.id === data.currency_id)?.symbol || '₹'}
                                                    </span>
                                                    <input
                                                        type="number"
                                                        className={`form-control ${errors.amount ? 'is-invalid' : ''}`}
                                                        value={data.amount}
                                                        onChange={(e) => setData('amount', e.target.value)}
                                                        step="0.01"
                                                        min="0.01"
                                                        required
                                                    />
                                                </div>
                                                {errors.amount && <div className="invalid-feedback d-block">{errors.amount}</div>}
                                            </div>

                                            {/* Currency */}
                                            <div className="col-md-6">
                                                <label className="form-label">Currency</label>
                                                <Select
                                                    options={currencyOptions}
                                                    value={currencyOptions.find(o => o.value === data.currency_id)}
                                                    onChange={(option) => setData('currency_id', option?.value || '')}
                                                    isClearable
                                                    placeholder="Select currency..."
                                                    classNamePrefix="react-select"
                                                />
                                            </div>

                                            {/* Payment Method */}
                                            <div className="col-md-6">
                                                <label className="form-label">Payment Method</label>
                                                <select
                                                    className="form-select"
                                                    value={data.payment_method}
                                                    onChange={(e) => setData('payment_method', e.target.value)}
                                                >
                                                    <option value="">Select method...</option>
                                                    {Object.entries(PAYMENT_METHODS).map(([key, { label }]) => (
                                                        <option key={key} value={key}>{label}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            {/* Due Date (for scheduled) */}
                                            {data.type === 'scheduled' && (
                                                <div className="col-md-6">
                                                    <label className="form-label">Due Date</label>
                                                    <DatePicker
                                                        selected={data.due_date}
                                                        onChange={(date) => setData('due_date', date)}
                                                        dateFormat="dd MMM yyyy"
                                                        className="form-control"
                                                        placeholderText="Select due date..."
                                                    />
                                                </div>
                                            )}

                                            {/* Payment Date (for received) */}
                                            {data.type === 'received' && (
                                                <div className="col-md-6">
                                                    <label className="form-label">Payment Date</label>
                                                    <DatePicker
                                                        selected={data.payment_date}
                                                        onChange={(date) => setData('payment_date', date)}
                                                        dateFormat="dd MMM yyyy"
                                                        className="form-control"
                                                        placeholderText="Select payment date..."
                                                        maxDate={new Date()}
                                                    />
                                                </div>
                                            )}

                                            {/* Reference Number */}
                                            <div className="col-md-6">
                                                <label className="form-label">Reference Number</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={data.reference_number}
                                                    onChange={(e) => setData('reference_number', e.target.value)}
                                                    placeholder="Transaction/Receipt number"
                                                />
                                            </div>

                                            {/* Bank Name (for bank transfer/cheque) */}
                                            {['bank_transfer', 'cheque'].includes(data.payment_method) && (
                                                <div className="col-md-6">
                                                    <label className="form-label">Bank Name</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={data.bank_name}
                                                        onChange={(e) => setData('bank_name', e.target.value)}
                                                        placeholder="Bank name"
                                                    />
                                                </div>
                                            )}

                                            {/* Cheque fields */}
                                            {data.payment_method === 'cheque' && (
                                                <>
                                                    <div className="col-md-6">
                                                        <label className="form-label">Cheque Number</label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            value={data.cheque_number}
                                                            onChange={(e) => setData('cheque_number', e.target.value)}
                                                            placeholder="Cheque number"
                                                        />
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label">Cheque Date</label>
                                                        <DatePicker
                                                            selected={data.cheque_date}
                                                            onChange={(date) => setData('cheque_date', date)}
                                                            dateFormat="dd MMM yyyy"
                                                            className="form-control"
                                                            placeholderText="Cheque date..."
                                                        />
                                                    </div>
                                                </>
                                            )}

                                            {/* Transaction ID (for online/UPI) */}
                                            {['online', 'upi', 'card'].includes(data.payment_method) && (
                                                <div className="col-md-6">
                                                    <label className="form-label">Transaction ID</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={data.transaction_id}
                                                        onChange={(e) => setData('transaction_id', e.target.value)}
                                                        placeholder="Transaction ID"
                                                    />
                                                </div>
                                            )}

                                            {/* Invoice Number */}
                                            <div className="col-md-6">
                                                <label className="form-label">Invoice Number</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={data.invoice_number}
                                                    onChange={(e) => setData('invoice_number', e.target.value)}
                                                    placeholder="Related invoice number"
                                                />
                                            </div>

                                            {/* Notes */}
                                            <div className="col-12">
                                                <label className="form-label">Notes</label>
                                                <textarea
                                                    className="form-control"
                                                    rows="2"
                                                    value={data.notes}
                                                    onChange={(e) => setData('notes', e.target.value)}
                                                    placeholder="Additional notes..."
                                                ></textarea>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="modal-footer border-0">
                                        <button
                                            type="button"
                                            className="btn btn-light"
                                            onClick={() => {
                                                setShowForm(false);
                                                setEditingPayment(null);
                                                reset();
                                            }}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-primary"
                                            disabled={processing}
                                        >
                                            {processing ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-1"></span>
                                                    Saving...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="bi bi-check-lg me-1"></i>
                                                    {editingPayment ? 'Update Payment' : 'Save Payment'}
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </motion.div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Status Change Modal */}
            <AnimatePresence>
                {showStatusModal && selectedPayment && (
                    <motion.div
                        className="modal show d-block"
                        style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <div className="modal-dialog modal-dialog-centered">
                            <motion.div
                                className="modal-content"
                                initial={{ scale: 0.9, y: 20 }}
                                animate={{ scale: 1, y: 0 }}
                                exit={{ scale: 0.9, y: 20 }}
                            >
                                <div className="modal-header border-0">
                                    <h5 className="modal-title">
                                        <i className="bi bi-arrow-repeat me-2"></i>
                                        Change Payment Status
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => {
                                            setShowStatusModal(false);
                                            setSelectedPayment(null);
                                        }}
                                    ></button>
                                </div>
                                <div className="modal-body">
                                    <p className="mb-3">
                                        Change status for <strong>{selectedPayment.payment_number}</strong>
                                    </p>
                                    <div className="d-flex flex-wrap gap-2">
                                        {Object.entries(STATUSES).map(([key, { label, color, icon }]) => (
                                            <button
                                                key={key}
                                                className={`btn btn-${selectedPayment.status === key ? color : `outline-${color}`}`}
                                                onClick={() => {
                                                    if (key === 'rejected') {
                                                        const reason = prompt('Enter rejection reason:');
                                                        if (reason) {
                                                            submitStatusChange(key, reason);
                                                        }
                                                    } else {
                                                        submitStatusChange(key);
                                                    }
                                                }}
                                                disabled={selectedPayment.status === key}
                                            >
                                                <i className={`bi ${icon} me-1`}></i>
                                                {label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Schedule Payments Modal */}
            <AnimatePresence>
                {showScheduleModal && (
                    <motion.div
                        className="modal show d-block"
                        style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <div className="modal-dialog modal-dialog-centered">
                            <motion.div
                                className="modal-content"
                                initial={{ scale: 0.9, y: 20 }}
                                animate={{ scale: 1, y: 0 }}
                                exit={{ scale: 0.9, y: 20 }}
                            >
                                <div className="modal-header border-0">
                                    <h5 className="modal-title">
                                        <i className="bi bi-calendar-plus me-2"></i>
                                        Schedule Payments from Quotation
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => {
                                            setShowScheduleModal(false);
                                            scheduleForm.reset();
                                        }}
                                    ></button>
                                </div>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label className="form-label">Select Quotation *</label>
                                        <Select
                                            options={quotationOptions}
                                            value={quotationOptions.find(o => o.value === scheduleForm.data.quotation_id)}
                                            onChange={(option) => scheduleForm.setData('quotation_id', option?.value || '')}
                                            placeholder="Select quotation..."
                                            classNamePrefix="react-select"
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Number of Installments *</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={scheduleForm.data.installments}
                                            onChange={(e) => scheduleForm.setData('installments', parseInt(e.target.value) || 1)}
                                            min="1"
                                            max="12"
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">First Due Date *</label>
                                        <DatePicker
                                            selected={scheduleForm.data.first_due_date}
                                            onChange={(date) => scheduleForm.setData('first_due_date', date)}
                                            dateFormat="dd MMM yyyy"
                                            className="form-control"
                                            minDate={new Date()}
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Days Between Installments</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={scheduleForm.data.interval_days}
                                            onChange={(e) => scheduleForm.setData('interval_days', parseInt(e.target.value) || 30)}
                                            min="1"
                                            max="365"
                                        />
                                        <small className="text-muted">Default: 30 days</small>
                                    </div>
                                </div>
                                <div className="modal-footer border-0">
                                    <button
                                        type="button"
                                        className="btn btn-light"
                                        onClick={() => {
                                            setShowScheduleModal(false);
                                            scheduleForm.reset();
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={handleSchedulePayments}
                                        disabled={!scheduleForm.data.quotation_id || scheduleForm.processing}
                                    >
                                        {scheduleForm.processing ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-1"></span>
                                                Creating...
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-calendar-plus me-1"></i>
                                                Create Schedule
                                            </>
                                        )}
                                    </button>
                                </div>
                            </motion.div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
